import { reactive } from 'vue'
import { englishKnownLanguage } from './voiceAudition'
import { clipCascadeEnabled } from './clipVoicePreference'
import type { KnownVoiceCatalogue } from '../../../core/src/audio/knownVoiceChoice'

const catalogues = reactive(new Map<string, KnownVoiceCatalogue>())
const choices = reactive(new Map<string, string>())
const pending = new Map<string, Promise<void>>()
const scopeKey = (course: string, learner?: string | null) => `ssi:known-voice:${learner || 'guest'}:${course}`
// A signed-in learner who picks Default on purpose is not "never chose": without a marker the
// empty slot would fall back to their old guest pick and undo the choice.
const EXPLICIT_DEFAULT = '__default__'
export function knownVoiceChoice(course: string, learner?: string | null): string {
  if (!englishKnownLanguage(course.split('_for_')[1] || '')) return ''
  const key = scopeKey(course, learner)
  if (!choices.has(key)) {
    try { choices.set(key, localStorage.getItem(key) || '') } catch { choices.set(key, '') }
  }
  // A guest's pick carries over on sign-up: until the learner chooses for
  // themselves, their own empty slot defers to what they chose as a guest.
  if (choices.get(key) === EXPLICIT_DEFAULT) return ''
  if (!choices.get(key) && learner) return knownVoiceChoice(course, null)
  return choices.get(key) || ''
}
export function setKnownVoiceChoice(course: string, learner: string | null | undefined, voice: string): void {
  const key = scopeKey(course, learner)
  const stored = !voice && learner ? EXPLICIT_DEFAULT : voice
  choices.set(key, stored)
  try { localStorage.setItem(key, stored) } catch { /* Preference still works this session. */ }
}
// Catalogue scope includes the learner: a free-preview catalogue must never
// persist into a different learner's session. Refresh each course mount; no
// persisted audio pointers survive content_version/audio_stamp changes.
export function knownVoiceCatalogue(course: string, learner?: string | null): KnownVoiceCatalogue | undefined {
  return catalogues.get(scopeKey(course, learner))
}
export async function loadKnownVoices(course: string, learner: string | null | undefined, token?: string): Promise<void> {
  if (!course || course === 'demo') return
  const key = scopeKey(course, learner)
  if (pending.has(key)) return pending.get(key)
  const request = (async () => {
    // Big courses (French, Spanish) take ~30s to assemble cold: a 30s abort
    // lost the catalogue and hid Prompt voice. Wait longer, and try once more.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await fetch(`/api/courses/${encodeURIComponent(course)}/known-voices${clipCascadeEnabled && navigator.onLine !== false ? '?menu=all' : ''}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}, signal: AbortSignal.timeout(90000),
        })
        if (!response.ok) continue
        const catalogue = await response.json()
        if (Array.isArray(catalogue.voices) && catalogue.clips) { catalogues.set(key, catalogue); return }
      } catch { /* Best effort. Default prompt audio always remains playable. */ }
    }
  })().finally(() => pending.delete(key))
  pending.set(key, request)
  return request
}
